import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";

import { api } from "../../api/client";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Badge, Button, Field, inputClass, textareaClass } from "../../components/ui";
import { k, t } from "../../i18n/i18n";
import { FormError } from "../AuthForm";
import { authDateTime, LOGIN_PATTERN } from "../authText";
import { adminErrorText, sizeLabel } from "./adminText";
import type { BatchPreview, CreatedAccount, IssuedCode, PendingFiled, UnitAccount } from "./adminTypes";
import { ReauthCancelled, withReauth } from "./reauth";
import { Waiting } from "../../components/Waiting";

/** Fenêtres de la vue « Comptes de l'unité » (docs/17 §5.3, §5.6, §8.3) : nouveau compte, modification, plusieurs
 *  comptes, désactivation avec transfert, éléments d'un compte. */

/** Identifiant proposé d'après le grade et nom, comme le serveur : « CNE Martin-Dupré » → « cne.martin-dupre ». */
export function suggestLogin(name: string): string {
  const ascii = name
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
  const words = ascii
    .split(/\s+/)
    .map((w) => w.replace(/[^a-z0-9-]/g, ""))
    .filter(Boolean);
  let base = words.join(".").slice(0, 36);
  if (!base) return "";
  if (base.length < 3) base = `${base}.cpt`;
  return base;
}

function failure(error: unknown): string | null {
  if (!error || error instanceof ReauthCancelled) return null;
  return adminErrorText(error);
}

/** Nouveau compte, ou modification d'un compte (grade et nom, fonction). */
export function AccountDialog({
  account,
  admins,
  onCreated,
  onSaved,
  onClose,
}: {
  account: UnitAccount | null;
  admins: number;
  onCreated: (created: CreatedAccount) => void;
  onSaved: () => void;
  onClose: () => void;
}) {
  const editing = account !== null;
  const [name, setName] = useState(account?.display_name ?? "");
  const [appointment, setAppointment] = useState(account?.appointment ?? "");
  const [login, setLogin] = useState(account?.login ?? "");
  const [loginTouched, setLoginTouched] = useState(editing);
  const [admin, setAdmin] = useState(false);
  const shownLogin = loginTouched ? login : suggestLogin(name);
  const save = useMutation({
    mutationFn: () =>
      withReauth<unknown>(() =>
        editing
          ? api.accounts.update(account.id, { version: account.version, display_name: name.trim(), appointment: appointment.trim() || null })
          : api.accounts.create({
              display_name: name.trim(),
              appointment: appointment.trim() || null,
              login: shownLogin.trim() || null,
              // Un seul administrateur : la nomination se demande à BELLICOM, le compte naît utilisateur.
              admin: admin && admins >= 2,
            }),
      ),
    onSuccess: (result) => {
      if (editing) onSaved();
      else onCreated(result as CreatedAccount);
    },
  });
  const nameMissing = name.trim().length < 2;
  const loginBad = !editing && shownLogin.length > 0 && !LOGIN_PATTERN.test(shownLogin);
  const missing = nameMissing ? "admin-account-name" : loginBad || (!editing && !shownLogin) ? "admin-account-login" : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (missing) return void designate(attentionSelector(missing));
    if (!save.isPending) save.mutate();
  };
  return (
    <Dialog
      title={editing ? t("Modifier le compte de {nom}", { nom: account.display_name }) : t("Nouveau compte")}
      width={460}
      onClose={onClose}
      dirty={!editing && Boolean(name || appointment)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          <Button type="submit" form="admin-account-form" disabled={save.isPending} blocked={Boolean(missing)} onBlocked={() => missing && designate(attentionSelector(missing))}>
            {save.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : editing ? t("Enregistrer") : t("Créer le compte")}
          </Button>
        </>
      }
    >
      <DialogBody>
        <form id="admin-account-form" onSubmit={submit} className="space-y-3">
          <Field label={t("Grade et nom")} hint={t("Ex. : CNE MARTIN. C'est le nom qui signe vos actions (journal DIREX, ordres reçus, transmissions).")}>
            <input data-attention="admin-account-name" className={inputClass} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <Field label={t("Fonction (facultatif)")}>
            <input className={inputClass} value={appointment} maxLength={80} onChange={(e) => setAppointment(e.target.value)} />
          </Field>
          {!editing && (
            <Field label={t("Identifiant")} hint={t("Ex. : cne.martin. Lettres sans accent, chiffres, point, tiret ; 3 à 40 caractères.")}>
              <input
                data-attention="admin-account-login"
                className={inputClass}
                value={shownLogin}
                maxLength={40}
                autoCapitalize="none"
                spellCheck={false}
                onChange={(e) => {
                  setLoginTouched(true);
                  setLogin(e.target.value.toLowerCase());
                }}
              />
            </Field>
          )}
          {!editing && (
            <>
              <Checkbox checked={admin} onChange={setAdmin} label={t("Administrateur de l'unité")} />
              <Presence show={admin}>
                {admin && (
                  <Callout tone="info">
                    {admins >= 2
                      ? t("Le compte est créé comme utilisateur ; sa nomination comme administrateur doit être approuvée par un autre administrateur.")
                      : t("Le deuxième administrateur de l'unité est nommé par BELLICOM, après vérification de son identité : demandez-le à BELLICOM en donnant l'identifiant de ce compte.")}
                  </Callout>
                )}
              </Presence>
            </>
          )}
          <FormError message={failure(save.error)} />
        </form>
      </DialogBody>
    </Dialog>
  );
}

/** Plusieurs comptes : tableau collé, une ligne par personne ; doublons signalés avant la création. */
export function BatchAccountsDialog({ onCreated, onClose }: { onCreated: (created: IssuedCode[]) => void; onClose: () => void }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [checkedText, setCheckedText] = useState("");
  const check = useMutation({
    mutationFn: () => api.accounts.batchCheck(text),
    onSuccess: (result) => {
      setPreview(result);
      setCheckedText(text);
    },
  });
  const create = useMutation({
    mutationFn: () => withReauth(() => api.accounts.batch(text)),
    onSuccess: (result) => onCreated(result.created),
  });
  const rows = preview?.rows ?? [];
  const current = preview !== null && checkedText === text;
  const problems = rows.filter((row) => row.problem).length;
  const tooMany = rows.length > (preview?.max ?? 50);
  const ready = current && rows.length > 0 && !problems && !tooMany;
  const lines = text.split("\n").filter((line) => line.trim()).length;
  const designateText = () => designate(attentionSelector("admin-batch-text"));
  const problemLabel = (problem: string | null) =>
    problem === "taken" ? t("Identifiant déjà pris") : problem === "login" ? t("Identifiant invalide") : problem ? t("Grade et nom manquant") : null;
  return (
    <Dialog
      title={t("Plusieurs comptes")}
      width={620}
      onClose={onClose}
      dirty={Boolean(text.trim())}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          {ready ? (
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              {create.isPending ? <Waiting>{t("Création…")}</Waiting> : t("Créer {n} comptes", { n: rows.length })}
            </Button>
          ) : (
            <Button onClick={() => check.mutate()} disabled={check.isPending} blocked={!lines} onBlocked={designateText}>
              {check.isPending ? <Waiting>{t("Vérification…")}</Waiting> : t("Vérifier les lignes")}
            </Button>
          )}
        </>
      }
    >
      <DialogBody>
        <Field label={t("Une ligne par personne : grade et nom ; fonction ; identifiant (facultatif). 50 lignes au plus.")}>
          <textarea
            data-attention="admin-batch-text"
            className={textareaClass}
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("CNE DUPONT ; S3\nLTN MOREL ; S2 ; ltn.morel")}
            spellCheck={false}
            autoFocus
          />
        </Field>
        <Presence show={current && rows.length > 0}>
          {current && rows.length > 0 && (
            <Motion origin="fade">
              <ul className="rounded-lg border border-line px-3">
                {rows.map((row, index) => (
                  <li key={index} className="grid min-h-8 grid-cols-[1fr_auto] items-center gap-x-3 border-b border-line py-1.5 last:border-b-0">
                    <span className="min-w-0">
                      <span className="block truncate text-ui text-ink">{row.display_name || "—"}</span>
                      <span className="block truncate text-meta text-ink-muted">
                        {[row.appointment, row.login].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </span>
                    {row.problem ? <Badge tone="danger" dot>{problemLabel(row.problem)}</Badge> : <Badge tone="ok">{t("Prêt")}</Badge>}
                  </li>
                ))}
              </ul>
            </Motion>
          )}
        </Presence>
        <Presence show={current && (problems > 0 || tooMany)}>
          {current && (problems > 0 || tooMany) && (
            <Callout tone="warn">
              {tooMany ? t("50 lignes au plus : répartissez les comptes en plusieurs fois.") : t("Corrigez les lignes signalées, puis vérifiez de nouveau.")}
            </Callout>
          )}
        </Presence>
        <FormError message={failure(check.error ?? create.error)} />
      </DialogBody>
    </Dialog>
  );
}

/** Désactiver le compte de {nom} : ses éléments changent de propriétaire, sans changer de partage. Sur un compte
 *  déjà désactivé : « Transférer ses éléments », seul le transfert est fait. */
export function DeactivateDialog({
  account,
  accounts,
  me,
  onDone,
  onClose,
}: {
  account: UnitAccount;
  accounts: UnitAccount[];
  me: string;
  onDone: (result: { account: UnitAccount } | PendingFiled) => void;
  onClose: () => void;
}) {
  const heirs = useMemo(() => accounts.filter((a) => a.status === "active" && a.id !== account.id), [accounts, account.id]);
  const [heir, setHeir] = useState(me);
  const transferOnly = account.status === "disabled";
  const deactivate = useMutation({
    mutationFn: () =>
      withReauth(() =>
        transferOnly ? api.accounts.transfer(account.id, heir === me ? null : heir) : api.accounts.deactivate(account.id, heir === me ? null : heir),
      ),
    onSuccess: onDone,
  });
  const items = account.items;
  const counts = { nom: account.display_name, a: items.exercises, b: items.odbs, c: items.zones };
  return (
    <Dialog
      title={transferOnly ? t("Transférer les éléments de {nom}", { nom: account.display_name }) : t("Désactiver le compte de {nom}", { nom: account.display_name })}
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          {transferOnly ? (
            <Button onClick={() => deactivate.mutate()} disabled={deactivate.isPending}>
              {deactivate.isPending ? <Waiting>{t("Transfert…")}</Waiting> : t("Transférer")}
            </Button>
          ) : (
            <Button variant="danger" onClick={() => deactivate.mutate()} disabled={deactivate.isPending}>
              {deactivate.isPending ? <Waiting>{t("Désactivation…")}</Waiting> : t("Désactiver")}
            </Button>
          )}
        </>
      }
    >
      <DialogBody>
        <p className="text-ui leading-relaxed text-ink-soft">
          {transferOnly
            ? t("Les éléments de {nom} ({a} dossiers, {b} ordres de bataille, {c} terrains) changent de propriétaire, sans changer de partage : un dossier privé reste privé. Le compte reste désactivé.", counts)
            : t("{nom} ne pourra plus se connecter et ses sessions seront fermées. Ses éléments ({a} dossiers, {b} ordres de bataille, {c} terrains) changent de propriétaire, sans changer de partage : un dossier privé reste privé.", counts)}
        </p>
        <Field label={t("Nouveau propriétaire")}>
          <Select value={heir} onChange={(e) => setHeir(e.target.value)} aria-label={t("Nouveau propriétaire")}>
            {heirs.map((a) => (
              <option key={a.id} value={a.id}>
                {a.id === me ? t("{compte} (vous, par défaut)", { compte: a.display_name }) : a.display_name}
              </option>
            ))}
          </Select>
        </Field>
        {account.role === "admin" && !transferOnly && (
          <Callout tone="info">
            {t("{action} pour {nom} : un second administrateur, autre que vous et que {nom}, doit l'approuver dans les 24 h. Sans lui, adressez-vous à BELLICOM.", {
              action: t("Désactiver le compte"),
              nom: account.display_name,
            })}
          </Callout>
        )}
        <FormError message={failure(deactivate.error)} />
      </DialogBody>
    </Dialog>
  );
}

const ITEM_KIND: Record<string, string> = { exercise: k("Dossier"), odb: k("Ordre de bataille"), zone: k("Terrain") };

/** Éléments d'un compte : nom, taille, dernière activité — jamais leur contenu (l'administrateur n'ouvre pas les
 *  dossiers privés d'un compte actif, §3.3). */
export function ItemsDialog({ account, onClose }: { account: UnitAccount; onClose: () => void }) {
  const items = useQuery({ queryKey: ["unit-account-items", account.id], queryFn: () => api.accounts.items(account.id), staleTime: 0 });
  const list = items.data?.items ?? [];
  return (
    <Dialog title={t("Éléments de {nom}", { nom: account.display_name })} subtitle={sizeLabel(account.items.bytes)} width={560} onClose={onClose}>
      <DialogBody>
        {items.error ? (
          <Callout tone="danger">{adminErrorText(items.error)}</Callout>
        ) : !items.data ? (
          <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>
        ) : list.length === 0 ? (
          <p className="text-ui text-ink-muted">{t("Ce compte ne possède aucun élément.")}</p>
        ) : (
          <Motion origin="fade">
            <ul>
              {list.map((item) => (
                <li key={`${item.kind}-${item.id}`} className="grid min-h-8 grid-cols-[1fr_auto] items-center gap-x-3 border-b border-line py-1.5 last:border-b-0">
                  <span className="min-w-0">
                    <span className="block truncate text-ui text-ink">{item.name || "—"}</span>
                    <span className="block text-meta text-ink-muted">
                      {t(ITEM_KIND[item.kind] ?? ITEM_KIND.exercise!)} · {t("Dernière activité : {when}", { when: authDateTime(item.updated_at) })}
                    </span>
                  </span>
                  <span className="text-meta text-ink-muted tabular-nums">{sizeLabel(item.size_bytes)}</span>
                </li>
              ))}
            </ul>
          </Motion>
        )}
        <p className="text-meta leading-snug text-ink-muted">
          {t("Vous voyez le nom, la taille et la dernière activité de ces éléments, jamais leur contenu. Ils vous reviennent si vous désactivez le compte.")}
        </p>
      </DialogBody>
    </Dialog>
  );
}
